'use client';

import { TechnologyOption } from '@/lib/tech-stack-types';
import { generateMultipleTrendData, formatLargeNumber } from '@/lib/visualization-data';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useState, useMemo } from 'react';

interface TechStackTrendChartsProps {
  technologies: TechnologyOption[];
}

const CHART_COLORS = ['#E89841', '#4A90E2', '#50C878', '#9B59B6', '#E74C3C'];

export default function TechStackTrendCharts({ technologies }: TechStackTrendChartsProps) {
  const [selectedTechnologies, setSelectedTechnologies] = useState<string[]>(
    technologies.slice(0, 3).map(t => t.id)
  );
  const [metric, setMetric] = useState<'github-stars' | 'npm-downloads' | 'stackoverflow-questions'>('github-stars');
  const [timeRange, setTimeRange] = useState<number>(12);
  const [useLogScale, setUseLogScale] = useState(false);

  // Generate trend data
  const trendData = useMemo(() => {
    const selected = technologies.filter(t => selectedTechnologies.includes(t.id));
    return generateMultipleTrendData(selected, timeRange, metric);
  }, [technologies, selectedTechnologies, timeRange, metric]);

  // Combine data for chart
  const chartData = useMemo(() => {
    if (trendData.length === 0) return [];

    const dates = trendData[0].data.map(d => d.date);
    return dates.map(date => {
      const point: any = { date };
      trendData.forEach(tech => {
        const dataPoint = tech.data.find(d => d.date === date);
        if (dataPoint) {
          point[tech.technologyName] = dataPoint.value;
        }
      });
      return point;
    });
  }, [trendData]);

  // Calculate growth rates
  const growthRates = useMemo(() => {
    return trendData.map(tech => {
      const firstValue = tech.data[0]?.value || 0;
      const lastValue = tech.data[tech.data.length - 1]?.value || 0;
      const growth = firstValue > 0 ? ((lastValue - firstValue) / firstValue) * 100 : 0;
      return {
        name: tech.technologyName,
        growth: growth.toFixed(1),
        direction: growth > 0 ? 'up' : growth < 0 ? 'down' : 'flat',
      };
    });
  }, [trendData]);

  const toggleTechnology = (techId: string) => {
    if (selectedTechnologies.includes(techId)) {
      if (selectedTechnologies.length > 1) {
        setSelectedTechnologies(selectedTechnologies.filter(id => id !== techId));
      }
    } else {
      if (selectedTechnologies.length < 5) {
        setSelectedTechnologies([...selectedTechnologies, techId]);
      }
    }
  };

  const metricLabels = {
    'github-stars': 'GitHub Stars',
    'npm-downloads': 'NPM Downloads (Weekly)',
    'stackoverflow-questions': 'Stack Overflow Questions',
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Title and Description */}
      <div>
        <h3 className="text-2xl font-bold text-foreground mb-2">Popularity Trends</h3>
        <p className="text-foreground-secondary text-sm">
          Track technology popularity over time across different metrics.
          Compare up to 5 technologies simultaneously.
        </p>
      </div>

      {/* Controls */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Metric Selector */}
        <div className="bg-surface rounded-lg p-4">
          <label className="text-xs font-semibold text-foreground-secondary mb-2 block">Metric</label>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value as any)}
            className="w-full bg-background text-foreground rounded px-3 py-2 text-sm border border-border focus:border-accent focus:outline-none"
          >
            <option value="github-stars">GitHub Stars</option>
            <option value="npm-downloads">NPM Downloads</option>
            <option value="stackoverflow-questions">Stack Overflow</option>
          </select>
        </div>

        {/* Time Range Selector */}
        <div className="bg-surface rounded-lg p-4">
          <label className="text-xs font-semibold text-foreground-secondary mb-2 block">Time Range</label>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="w-full bg-background text-foreground rounded px-3 py-2 text-sm border border-border focus:border-accent focus:outline-none"
          >
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
            <option value={24}>Last 24 months</option>
          </select>
        </div>

        {/* Log Scale Toggle */}
        <div className="bg-surface rounded-lg p-4">
          <label className="text-xs font-semibold text-foreground-secondary mb-2 block">Scale</label>
          <button
            onClick={() => setUseLogScale(!useLogScale)}
            className={`w-full px-3 py-2 rounded text-sm font-medium transition-colors ${
              useLogScale
                ? 'bg-accent text-accent-foreground'
                : 'bg-background text-foreground border border-border'
            }`}
          >
            {useLogScale ? 'Logarithmic' : 'Linear'}
          </button>
        </div>

        {/* Selected Count */}
        <div className="bg-surface rounded-lg p-4">
          <div className="text-xs font-semibold text-foreground-secondary mb-1">Selected</div>
          <div className="text-2xl font-bold text-accent">
            {selectedTechnologies.length}/5
          </div>
          <div className="text-xs text-foreground-tertiary">technologies</div>
        </div>
      </div>

      {/* Technology Selector */}
      <div className="bg-surface rounded-lg p-4">
        <label className="text-sm font-semibold text-foreground-secondary mb-3 block">
          Select Technologies to Compare (max 5)
        </label>
        <div className="flex flex-wrap gap-2">
          {technologies.map((tech, index) => {
            const isSelected = selectedTechnologies.includes(tech.id);
            const color = CHART_COLORS[selectedTechnologies.indexOf(tech.id)];

            return (
              <button
                key={tech.id}
                onClick={() => toggleTechnology(tech.id)}
                disabled={!isSelected && selectedTechnologies.length >= 5}
                className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                  isSelected
                    ? 'text-accent-foreground border-2'
                    : selectedTechnologies.length >= 5
                    ? 'bg-background/50 text-foreground-muted cursor-not-allowed'
                    : 'bg-background text-foreground-secondary hover:bg-background/80 border border-border'
                }`}
                style={isSelected ? { backgroundColor: color, borderColor: color } : {}}
              >
                {tech.name}
                {isSelected && ' ✓'}
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      <div className="bg-surface rounded-lg p-6">
        <h4 className="text-lg font-semibold text-foreground mb-4">
          {metricLabels[metric]} Over Time
        </h4>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid stroke="#E8D5B7" strokeOpacity={0.1} strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tick={{ fill: '#E8D5B7', fontSize: 11 }}
              tickFormatter={formatDate}
            />
            <YAxis
              scale={useLogScale ? 'log' : 'linear'}
              domain={useLogScale ? ['auto', 'auto'] : [0, 'auto']}
              tick={{ fill: '#E8D5B7', fontSize: 11 }}
              tickFormatter={(value) => formatLargeNumber(value)}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#0A1E2E',
                border: '1px solid #E8D5B7',
                borderRadius: '8px',
                color: '#E8D5B7',
              }}
              labelFormatter={(label) => formatDate(label as string)}
              formatter={(value: number | undefined) => [formatLargeNumber(value ?? 0), '']}
            />
            <Legend
              wrapperStyle={{ color: '#E8D5B7' }}
              iconType="line"
            />
            {trendData.map((tech, index) => (
              <Line
                key={tech.technologyId}
                type="monotone"
                dataKey={tech.technologyName}
                stroke={CHART_COLORS[index]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Growth Rates */}
      <div className="bg-surface rounded-lg p-6">
        <h4 className="text-lg font-semibold text-foreground mb-4">
          Growth Rate ({timeRange} months)
        </h4>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {growthRates.map((rate, index) => (
            <div
              key={rate.name}
              className="bg-background rounded-lg p-4 border-l-4"
              style={{ borderColor: CHART_COLORS[index] }}
            >
              <div className="text-sm font-medium text-foreground mb-2">{rate.name}</div>
              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-bold ${
                  rate.direction === 'up' ? 'text-green-500' :
                  rate.direction === 'down' ? 'text-red-500' : 'text-foreground'
                }`}>
                  {rate.growth}%
                </span>
                <span className="text-sm text-foreground-tertiary">
                  {rate.direction === 'up' ? '↗' : rate.direction === 'down' ? '↘' : '→'}
                </span>
              </div>
              <div className="text-xs text-foreground-tertiary mt-1">
                {rate.direction === 'up' ? 'Growing' :
                 rate.direction === 'down' ? 'Declining' : 'Stable'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Data Source Note */}
      <p className="text-xs text-foreground-muted text-center">
        Trends derived from technology metadata and popularity indicators in our database.
      </p>
    </div>
  );
}
